/**
 * Modal creates popup which on mobile layout fills the entire visible page.
 *
 * Example:
 * <Parent>
 *   <Modal id="UniqueIdForThisModal" isOpen={this.state.modalIsOpen} onClose={handleClose}>
 *     <FormX />
 *   </Modal>
 * </Parent>
 */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { intlShape, injectIntl } from '../../util/reactIntl';
import { Button, IconClose } from '../../components';

import css from './Modal.module.css';

const KEY_CODE_ESCAPE = 27;

class Portal extends React.Component {
  constructor(props) {
    super(props);
    this.el = document.createElement('div');
  }

  componentDidMount() {
    // The portal element is inserted in the DOM tree after
    // the Modal's children are mounted, meaning that children
    // will be mounted on a detached DOM node. If a child
    // component requires to be attached to the DOM tree
    // immediately when mounted, for example to measure a
    // DOM node, or uses 'autoFocus' in a descendant, add
    // state to Modal and only render the children when Modal
    // is inserted in the DOM tree.
    this.props.portalRoot.appendChild(this.el);
  }

  componentWillUnmount() {
    this.props.portalRoot.removeChild(this.el);
  }

  render() {
    return ReactDOM.createPortal(this.props.children, this.el);
  }
}

export class ModalComponent extends Component {
  constructor(props) {
    super(props);
    this.handleBodyKeyUp = this.handleBodyKeyUp.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleScrollLayerMouseDown = this.handleScrollLayerMouseDown.bind(this);
    this.handleContainerKeyDown = this.handleContainerKeyDown.bind(this);

    this.refDiv = React.createRef();
    this.vh = null;
    this.previouslyFocusedElement = null;

    this.state = {
      portalRoot: null,
    };
  }

  componentDidMount() {
    const { id, isOpen, onManageDisableScrolling, restoreFocusOnClose } = this.props;
    onManageDisableScrolling(id, isOpen);
    window.document.body.addEventListener('keyup', this.handleBodyKeyUp);

    // A hack to update container height for mobile Safari,
    // when resizing happens due to scroll.
    // css.isOpenInPortal has "height: calc(var(--vh, 1vh) * 100)"
    this.vh = window.innerHeight * 0.01;
    window.document.documentElement.style.setProperty('--vh', `${this.vh}px`);
    window.addEventListener('resize', this.handleResize);

    if (isOpen && restoreFocusOnClose) {
      this.previouslyFocusedElement = window.document.activeElement;
    }

    this.setState({
      portalRoot: document.getElementById('portal-root'),
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { id, isOpen, onManageDisableScrolling } = this.props;

    if (isOpen !== prevProps.isOpen) {
      onManageDisableScrolling(id, isOpen);

      if (isOpen) {
        if (this.props.restoreFocusOnClose) {
          this.previouslyFocusedElement = window.document.activeElement;
        }
        this.focusDialogContent();
      } else if (prevProps.isOpen && this.props.restoreFocusOnClose) {
        this.restoreFocus();
      }
    } else if (
      isOpen &&
      this.props.usePortal &&
      !prevState.portalRoot &&
      this.state.portalRoot
    ) {
      this.focusDialogContent();
    }
  }

  componentWillUnmount() {
    const { id, onManageDisableScrolling } = this.props;
    window.document.body.removeEventListener('keyup', this.handleBodyKeyUp);
    window.removeEventListener('resize', this.handleResize);
    onManageDisableScrolling(id, false);
    this.restoreFocus();
  }

  handleBodyKeyUp(event) {
    const { isOpen, closeOnEscape } = this.props;
    if (event.keyCode === KEY_CODE_ESCAPE && isOpen && closeOnEscape) {
      this.handleClose(event);
    }
  }

  handleClose(event) {
    const { id, onClose, onManageDisableScrolling } = this.props;
    onManageDisableScrolling(id, false);
    onClose(event);
  }

  handleResize() {
    this.vh = window.innerHeight * 0.01;
    window.document.documentElement.style.setProperty('--vh', `${this.vh}px`);
  }

  getFocusableElements(container) {
    if (!container) return [];
    const selectors = [
      'a[href]',
      'area[href]',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'iframe',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ];

    return Array.from(container.querySelectorAll(selectors.join(','))).filter(element => {
      const isAriaHidden = element.getAttribute('aria-hidden') === 'true';
      if (isAriaHidden) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  focusDialogContent() {
    const focusTarget = () => {
      const container = this.refDiv.current;
      if (!container) return;
      const focusableElements = this.getFocusableElements(container);
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      } else {
        container.focus();
      }
    };

    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(focusTarget);
    } else {
      focusTarget();
    }
  }

  restoreFocus() {
    if (!this.props.restoreFocusOnClose) {
      return;
    }

    const previouslyFocusedElement = this.previouslyFocusedElement;
    this.previouslyFocusedElement = null;

    if (
      previouslyFocusedElement &&
      typeof previouslyFocusedElement.focus === 'function' &&
      window.document.contains(previouslyFocusedElement)
    ) {
      previouslyFocusedElement.focus();
    }
  }

  handleScrollLayerMouseDown(event) {
    if (this.props.closeOnBackdropClick && event.target === event.currentTarget) {
      this.handleClose(event);
    }
  }

  handleContainerKeyDown(event) {
    if (!this.props.isOpen || !this.props.trapFocus) {
      return;
    }

    const isTabKey = event.key === 'Tab' || event.keyCode === 9;
    if (!isTabKey) {
      return;
    }

    const container = this.refDiv.current;
    const focusableElements = this.getFocusableElements(container);

    if (focusableElements.length === 0) {
      event.preventDefault();
      container?.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = window.document.activeElement;

    if (event.shiftKey) {
      if (activeElement === firstElement || !focusableElements.includes(activeElement)) {
        event.preventDefault();
        lastElement.focus();
      }
    } else if (activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  render() {
    const {
      children,
      className,
      scrollLayerClassName,
      containerClassName,
      contentClassName,
      lightCloseButton,
      intl,
      isClosedClassName,
      isOpen,
      ismobileMenuModal,
      usePortal,
      isFullScreenVideoModal,
      hideCloseButton,
      isConfirmationModal,
      isEnrollModal,
      isTaskModal,
      modalTitle,
      modalActions,
      selectedGuideId,
      isCustomerTypeModal,
      isCancelSubscriptionModal,
      isChangePlanModal,
      isTOSModal = false,
      id,
    } = this.props;

    const closeModalMessage = intl.formatMessage({ id: 'Modal.closeModal' });
    const closeButtonClasses = classNames(css.close, {
      [css.closeLight]: lightCloseButton,
    });
    const closeBtn =
      isOpen && !hideCloseButton ? (
        <Button
          onClick={this.handleClose}
          rootClassName={closeButtonClasses}
          title={closeModalMessage}
        >
          {/* <span className={css.closeText}>
            {closeButtonMessage || <FormattedMessage id="Modal.close" />}
          </span> */}
          {ismobileMenuModal ? (
            <div className={css.mobileCloseIconWrapper}>
              <IconClose rootClassName={css.closeIcon} />
            </div>
          ) : (
            <IconClose rootClassName={css.closeIcon} />
          )}
        </Button>
      ) : null;

    // Modal uses given styles to wrap child components.
    // If props doesn't contain isClosedClassName, styles default to css.isClosed
    // This makes it possible to create ModalInMobile on top of Modal where style modes are:
    // visible, hidden, or none (ModalInMobile's children are always visible on desktop layout.)
    const isOpenClass = usePortal ? css.isOpenInPortal : css.isOpenInPlace;
    const modalClass = isOpen ? isOpenClass : isClosedClassName;
    const classes = classNames(
      modalClass,
      className,
      ismobileMenuModal ? css.mobileMenuModal : null,
      isFullScreenVideoModal ? css.fullScreenVideoModal : null,
      isConfirmationModal ? css.confirmationModal : null,
      isEnrollModal ? css.enrollModal : null,
      isTaskModal ? css.taskModal : null,
      selectedGuideId ? css.selectedGuideId : null,
      isCustomerTypeModal ? css.customerTypeModal : null,
      isCancelSubscriptionModal ? css.cancelSubscriptionModal : null,
      isChangePlanModal ? css.changeSubscriptionModal : null
    );
    const scrollLayerClasses = classNames(
      css.scrollLayer,
      { [css.tosScrollLayer]: isTOSModal },
      scrollLayerClassName
    );
    const containerClasses = containerClassName || css.container;
    const portalRoot = this.state.portalRoot;
    const titleId = modalTitle && id ? `${id}-title` : undefined;

    // If you want to use Portal https://reactjs.org/docs/portals.html
    // you need to use 'userPortal' flag.
    // ModalInMobile component needs to use the old Modal without the portal
    // because it's relying that the content is rendered inside
    // the DOM hierarchy of the parent component unlike Modal inside Portal.

    return !usePortal ? (
      <div className={classes}>
        <div className={scrollLayerClasses} onMouseDown={this.handleScrollLayerMouseDown}>
          <div
            className={classNames(containerClasses, css.focusedDiv)}
            ref={this.refDiv}
            tabIndex="-1"
            onKeyDown={this.handleContainerKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
          >
            {modalTitle ? (
              <div className={css.modalHeader}>
                <h2 id={titleId}>{modalTitle}</h2>
                {modalActions ? modalActions : closeBtn}
              </div>
            ) : (
              <>{!isCancelSubscriptionModal && closeBtn}</>
            )}
            <div className={classNames(contentClassName || css.content)}>{children}</div>
          </div>
        </div>
      </div>
    ) : portalRoot ? (
      <Portal portalRoot={portalRoot}>
        <div className={classes}>
          <div className={scrollLayerClasses} onMouseDown={this.handleScrollLayerMouseDown}>
            <div
              className={classNames(containerClasses, css.focusedDiv)}
              ref={this.refDiv}
              tabIndex="-1"
              onKeyDown={this.handleContainerKeyDown}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
            >
              {modalTitle ? (
                <div className={css.modalHeader}>
                  <h2 id={titleId}>{modalTitle}</h2>
                  {modalActions ? modalActions : closeBtn}
                </div>
              ) : (
                <>{!isCancelSubscriptionModal && closeBtn}</>
              )}
              <div className={classNames(contentClassName || css.content)}>{children}</div>
            </div>
          </div>
        </div>
      </Portal>
    ) : null;
  }
}

ModalComponent.defaultProps = {
  children: null,
  className: null,
  scrollLayerClassName: null,
  closeButtonMessage: null,
  containerClassName: null,
  contentClassName: null,
  lightCloseButton: false,
  isClosedClassName: css.isClosed,
  isOpen: false,
  onClose: null,
  usePortal: false,
  closeOnBackdropClick: false,
  closeOnEscape: true,
  trapFocus: false,
  restoreFocusOnClose: false,
};

const { bool, func, node, string } = PropTypes;

ModalComponent.propTypes = {
  children: node,
  className: string,
  scrollLayerClassName: string,
  closeButtonMessage: node,
  containerClassName: string,
  contentClassName: string,
  lightCloseButton: bool,
  id: string,
  intl: intlShape.isRequired,
  isClosedClassName: string,
  isOpen: bool,
  onClose: func.isRequired,
  usePortal: bool,
  closeOnBackdropClick: bool,
  closeOnEscape: bool,
  trapFocus: bool,
  restoreFocusOnClose: bool,

  // eslint-disable-next-line react/no-unused-prop-types
  onManageDisableScrolling: func.isRequired,
};

export default injectIntl(ModalComponent);
